import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bold,
  Code,
  Code2,
  Columns2,
  Eye,
  Heading1,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Lightbulb,
  Link2,
  List,
  ListOrdered,
  Minus,
  Pencil,
  Quote,
  Strikethrough,
} from 'lucide-react';
import TutorialMarkdown from './TutorialMarkdown';
import TutorialEditorPreview from './TutorialEditorPreview';
import TutorialLinkInsertDialog from './TutorialLinkInsertDialog';
import { buildImageLine, normalizeTutorialImageSrc } from '@/pages/tutorial/utils/tutorialMarkdownImages';

const MODES = {
  EDIT: 'edit',
  PREVIEW: 'preview',
  SPLIT: 'split',
};

function insertAroundSelection(textarea, before, after = before, placeholder = '') {
  const el = textarea;
  if (!el) return null;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const selected = el.value.slice(start, end) || placeholder;
  const nextValue = `${el.value.slice(0, start)}${before}${selected}${after}${el.value.slice(end)}`;
  const cursor = start + before.length + selected.length + after.length;
  return { nextValue, cursor };
}

function insertBlock(textarea, block) {
  const el = textarea;
  if (!el) return null;
  const start = el.selectionStart ?? 0;
  const prefix = start > 0 && el.value[start - 1] !== '\n' ? '\n\n' : '';
  const nextValue = `${el.value.slice(0, start)}${prefix}${block}\n${el.value.slice(start)}`;
  const cursor = start + prefix.length + block.length + 1;
  return { nextValue, cursor };
}

function ToolbarDivider() {
  return <span className="tutorial-md-editor__tool-divider" aria-hidden="true" />;
}

function ToolbarButton({ title, onClick, children, active = false }) {
  return (
    <button
      type="button"
      className={`tutorial-md-editor__tool${active ? ' tutorial-md-editor__tool--active' : ''}`}
      title={title}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function TutorialMarkdownEditor({
  value,
  onChange,
  onUploadImage,
  uploadingImage = false,
  minRows = 18,
  defaultMode = MODES.SPLIT,
}) {
  const textareaRef = useRef(null);
  const [mode, setMode] = useState(defaultMode);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const applyEdit = useCallback((result) => {
    if (!result) return;
    const el = textareaRef.current;
    if (!el) return;

    const scrollTop = el.scrollTop;
    const scrollLeft = el.scrollLeft;

    onChange(result.nextValue);

    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;
      textarea.focus({ preventScroll: true });
      textarea.setSelectionRange(result.cursor, result.cursor);
    });
  }, [onChange]);

  const wrapSelection = useCallback((before, after, placeholder) => {
    applyEdit(insertAroundSelection(textareaRef.current, before, after, placeholder));
  }, [applyEdit]);

  const appendBlock = useCallback((block) => {
    applyEdit(insertBlock(textareaRef.current, block));
  }, [applyEdit]);

  const handleLink = useCallback(() => {
    setLinkDialogOpen(true);
  }, []);

  const confirmLink = useCallback((url) => {
    if (!url) return;
    setLinkDialogOpen(false);
    wrapSelection('[', `](${url})`, '链接文字');
  }, [wrapSelection]);

  const cancelLink = useCallback(() => {
    setLinkDialogOpen(false);
  }, []);

  const handleImageUpload = useCallback(async () => {
    if (!onUploadImage) {
      const url = window.prompt('请输入图片地址', '/uploads/');
      if (!url) return;
      const alt = window.prompt('图片描述（可选）', '教程截图') || '教程截图';
      appendBlock(`![${alt}](${url})`);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,image/gif';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await onUploadImage(file);
        if (!url) return;
        const alt = file.name.replace(/\.[^.]+$/, '') || '教程截图';
        appendBlock(buildImageLine({
          src: normalizeTutorialImageSrc(url),
          alt,
          width: null,
          align: 'center',
        }));
      } catch (err) {
        window.alert(err.message || '图片上传失败');
      }
    };
    input.click();
  }, [appendBlock, onUploadImage]);

  const showEditor = mode === MODES.EDIT || mode === MODES.SPLIT;
  const showPreview = mode === MODES.PREVIEW || mode === MODES.SPLIT;

  const modeButtons = useMemo(() => ([
    { key: MODES.EDIT, label: '编辑', icon: Pencil },
    { key: MODES.PREVIEW, label: '预览', icon: Eye },
    { key: MODES.SPLIT, label: '分栏', icon: Columns2 },
  ]), []);

  return (
    <div className="tutorial-md-editor">
      <div className="tutorial-md-editor__toolbar">
        <div className="tutorial-md-editor__tools">
          <ToolbarButton title="一级标题" onClick={() => appendBlock('# 章节标题')}>
            <Heading1 size={16} />
          </ToolbarButton>
          <ToolbarButton title="二级标题" onClick={() => appendBlock('## 小节标题')}>
            <Heading2 size={16} />
          </ToolbarButton>
          <ToolbarButton title="三级标题" onClick={() => appendBlock('### 步骤标题')}>
            <Heading3 size={16} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton title="加粗" onClick={() => wrapSelection('**', '**', '重点内容')}>
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton title="斜体" onClick={() => wrapSelection('*', '*', '强调内容')}>
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton title="删除线" onClick={() => wrapSelection('~~', '~~', '删除内容')}>
            <Strikethrough size={16} />
          </ToolbarButton>
          <ToolbarButton title="行内代码" onClick={() => wrapSelection('`', '`', 'code')}>
            <Code size={16} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton title="无序列表" onClick={() => appendBlock('- 列表项')}>
            <List size={16} />
          </ToolbarButton>
          <ToolbarButton title="有序列表" onClick={() => appendBlock('1. 第一步')}>
            <ListOrdered size={16} />
          </ToolbarButton>
          <ToolbarButton title="引用" onClick={() => appendBlock('> 引用内容')}>
            <Quote size={16} />
          </ToolbarButton>
          <ToolbarButton title="分隔线" onClick={() => appendBlock('---')}>
            <Minus size={16} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton title="提示框" onClick={() => appendBlock('> **提示**：请在此填写注意事项')}>
            <Lightbulb size={16} />
          </ToolbarButton>
          <ToolbarButton title="注意框" onClick={() => appendBlock('> **注意**：请在此填写重要警告')}>
            <AlertTriangle size={16} />
          </ToolbarButton>
          <ToolbarButton title="代码块" onClick={() => appendBlock('```\n示例代码\n```')}>
            <Code2 size={16} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton title="插入链接" onClick={handleLink}>
            <Link2 size={16} />
          </ToolbarButton>
          <ToolbarButton title="插入图片" onClick={handleImageUpload}>
            <ImagePlus size={16} />
          </ToolbarButton>
        </div>
        <div className="tutorial-md-editor__modes">
          {modeButtons.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                className={`tutorial-md-editor__mode${mode === item.key ? ' tutorial-md-editor__mode--active' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setMode(item.key)}
              >
                <Icon size={14} />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className={`tutorial-md-editor__body tutorial-md-editor__body--${mode}`}>
        {showEditor ? (
          <div className="tutorial-md-editor__pane">
            <textarea
              ref={textareaRef}
              className="tutorial-md-editor__textarea"
              rows={minRows}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="支持 Markdown：标题、列表、加粗、斜体、代码块、提示框、图片与链接…"
            />
            {uploadingImage ? <div className="tutorial-md-editor__uploading">图片上传中…</div> : null}
          </div>
        ) : null}
        {showPreview ? (
          <div className="tutorial-md-editor__pane tutorial-md-editor__preview">
            {onChange ? (
              <TutorialEditorPreview content={value} onChange={onChange} />
            ) : (
              <TutorialMarkdown content={value} />
            )}
          </div>
        ) : null}
      </div>

      <TutorialLinkInsertDialog
        open={linkDialogOpen}
        onConfirm={confirmLink}
        onCancel={cancelLink}
      />
    </div>
  );
}
